import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import styled from "@emotion/styled";
import { iconWidth, InteractiveElement } from "@iventis/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ImageFullScreenViewer, StyledCustomDialog } from "@iventis/components";
import { isValidUuid } from "@iventis/utilities";
import { useCommentsService } from "./comments-service";

interface CommentImageProps {
    imageId: string;
    fullScreenIcon: boolean;
    className?: string;
}

export const CommentImageComponent = ({ imageId, fullScreenIcon, className }: CommentImageProps) => {
    const { getCommentImage } = useCommentsService();
    const [toggleFullScreen, setToggleFullScreen] = useState(false);

    const isUUID = isValidUuid(imageId);
    const { data } = useQuery(["comment-image", imageId], () => getCommentImage(imageId), { enabled: isUUID });

    if (isUUID && data == null) {
        return null;
    }

    return (
        <CommentImageContainer className={className}>
            <div className="image-icon-container" data-testid="image-icon-container">
                {isUUID ? <CommentImage alt={data?.name} src={data?.url} /> : <CommentImage alt={data?.name} src={imageId} />}
                {fullScreenIcon && (
                    <InteractiveElement className="expand-button" onClick={() => setToggleFullScreen(true)}>
                        <FontAwesomeIcon size="lg" icon={["far", "arrow-up-right-and-arrow-down-left-from-center"]} />
                    </InteractiveElement>
                )}
            </div>
            {toggleFullScreen && (
                <StyledCustomDialog $overflow="hidden" maxWidth="lg" open={toggleFullScreen} onClose={() => setToggleFullScreen(false)}>
                    <ImageFullScreenViewer name={data.name} imageUrl={data.url} onClose={() => setToggleFullScreen(false)} />
                </StyledCustomDialog>
            )}
        </CommentImageContainer>
    );
};

const CommentImageContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    .image-icon-container {
        position: relative;
    }

    .expand-button {
        box-sizing: border-box;
        position: absolute;
        right: 5px;
        top: 5px;
        // 4px less than the height as the icon is 4px bigger in width than height
        width: calc(${iconWidth.large} - 4px);
        height: ${iconWidth.large};
        background-color: ${({ theme }) => theme.typographyColors.blank};
        border-radius: 4px;
    }
`;

const CommentImage = styled.img`
    max-width: 100%;
    max-height: 200px;
    object-fit: contain;
`;
